import { z } from 'zod';

/** Продажа билетов по чеку. */
export const TicketPaymentRequestStatus = z.enum(['PENDING', 'APPROVED', 'REJECTED']);
export type TicketPaymentRequestStatus = z.infer<typeof TicketPaymentRequestStatus>;

/** Реквизиты: карта, банк, крипта — свободный текст. Пустая строка — ошибка; выключить продажу — null. */
export const PaymentDetailsText = z.string().trim().min(1).max(1000);

export const MyTicketPaymentDto = z.object({
  eventId: z.uuid(),
  /** null при salePaused или closed: непроверенные/неактуальные реквизиты гостю не показываем. */
  details: z.string().nullable(),
  amount: z.number().int().positive(),
  currency: z.string().length(3),
  /** Есть живой билет: бронь CONFIRMED или билет ACTIVE/CHECKED_IN — покупать повторно нельзя. */
  hasTicket: z.boolean(),
  /** maxAttendees != null && attendanceCount + активные холды >= maxAttendees — «мест нет» до перевода денег. */
  soldOut: z.boolean(),
  /** Событие на повторной модерации — продажа приостановлена до решения модератора. */
  salePaused: z.boolean(),
  /** День события прошёл — отправить чек уже нельзя. */
  closed: z.boolean(),
  myRequest: z
    .object({
      id: z.uuid(),
      status: TicketPaymentRequestStatus,
      rejectionReason: z.string().nullable(),
      createdAt: z.iso.datetime(),
    })
    .nullable(),
  /** Когда можно отправить следующий чек; null — можно сейчас (или заявка на проверке). */
  nextSubmitAt: z.iso.datetime().nullable(),
});
export type MyTicketPaymentDto = z.infer<typeof MyTicketPaymentDto>;

export const OrganizerTicketPaymentDto = z.object({
  id: z.uuid(),
  event: z.object({ id: z.uuid(), title: z.string(), startsAt: z.iso.datetime() }),
  guest: z.object({
    id: z.uuid(),
    firstName: z.string().nullable(),
    lastName: z.string().nullable(),
    username: z.string().nullable(),
    photoUrl: z.string().nullable(),
  }),
  amount: z.number().int(),
  currency: z.string().length(3),
  /** Presigned URL на 1 час; null — чек удалён по сроку хранения. */
  proofUrl: z.string().nullable(),
  createdAt: z.iso.datetime(),
  seatHeld: z.boolean(),
  overCapacity: z.boolean(),
});
export type OrganizerTicketPaymentDto = z.infer<typeof OrganizerTicketPaymentDto>;

export const OrganizerTicketPaymentDetailDto = OrganizerTicketPaymentDto.extend({
  status: TicketPaymentRequestStatus,
  rejectionReason: z.string().nullable(),
});
export type OrganizerTicketPaymentDetailDto = z.infer<typeof OrganizerTicketPaymentDetailDto>;

export const OrganizerTicketPaymentsResponse = z.object({ items: z.array(OrganizerTicketPaymentDto) });
export type OrganizerTicketPaymentsResponse = z.infer<typeof OrganizerTicketPaymentsResponse>;

export const ApproveTicketPaymentInput = z.object({ allowOverCapacity: z.boolean().optional() });
export type ApproveTicketPaymentInput = z.infer<typeof ApproveTicketPaymentInput>;

export const RejectTicketPaymentInput = z.object({ reason: z.string().trim().min(1).max(500) });
export type RejectTicketPaymentInput = z.infer<typeof RejectTicketPaymentInput>;

export const PaymentDetailsDto = z.object({ details: z.string().nullable() });
export type PaymentDetailsDto = z.infer<typeof PaymentDetailsDto>;

export const UpdatePaymentDetailsInput = z.object({ details: PaymentDetailsText.nullable() });
export type UpdatePaymentDetailsInput = z.infer<typeof UpdatePaymentDetailsInput>;
